/* eslint-disable import/no-unresolved */
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import whatsappIcon from 'images/dashboard/funnel/whatsapp.png';

import IconMC from '../../../../../../../components/IconMC';
import RightModalLayout from '../../../../../../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../../../../../../constants/actionsConstants';
import { useTools } from '../../../hooks/useTools';
import DescriptionWithToolPills from '../../components/DescriptionWithToolPills';
import {
  FLOW_BRANCH_CHANNEL,
  OVERVIEW_BRANCH_FILTER,
  channelButtonClass,
  filterOverviewLanes,
  intentionOverviewLanes,
  visibleOverviewLanes
} from '../../utils/flowBranches';
import GlobalInstructionSavedCard from './GlobalInstructionSavedCard';
import { AgentProvider } from '../../AgentContext';

const COMPONENT_KEY = 'GlobalInstructionModal';

const OverviewListRow = ({ children }) => (
  <div className="tw-flex tw-w-full tw-items-start tw-gap-3">
    <div
      className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100"
      aria-hidden
    >
      <IconMC name="layers-outline" size="sm" className="tw-text-gray-500" />
    </div>
    <div className="tw-min-w-0 tw-flex-1">{children}</div>
  </div>
);

const StaticSectionTitle = ({ children }) => (
  <div className="tw-text-sm tw-font-semibold tw-text-gray-900">
    {children}
  </div>
);

const renderStepDescriptionWithPills = (step, getToolDetails, t) => (
  <DescriptionWithToolPills
    description={step.description || ''}
    tools={step.tools || []}
    getToolDetails={getToolDetails}
    t={t}
    partKeyPrefix={String(step.id || 'step')}
  />
);

const BranchOverviewFilter = ({ value, onChange, t }) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
    <button
      type="button"
      aria-pressed={value === OVERVIEW_BRANCH_FILTER.ALL_LANES}
      className={channelButtonClass(value === OVERVIEW_BRANCH_FILTER.ALL_LANES)}
      onClick={() => onChange(OVERVIEW_BRANCH_FILTER.ALL_LANES)}
    >
      {t('config.intention_editor.branches.filter_all')}
    </button>
    <button
      type="button"
      aria-pressed={value === OVERVIEW_BRANCH_FILTER.ALL}
      className={channelButtonClass(value === OVERVIEW_BRANCH_FILTER.ALL)}
      onClick={() => onChange(OVERVIEW_BRANCH_FILTER.ALL)}
    >
      <IconMC name="world" size="sm" className="tw-text-current" />
      {t('config.intention_editor.branches.all')}
    </button>
    <button
      type="button"
      aria-pressed={value === OVERVIEW_BRANCH_FILTER.WHATSAPP}
      className={channelButtonClass(value === OVERVIEW_BRANCH_FILTER.WHATSAPP)}
      onClick={() => onChange(OVERVIEW_BRANCH_FILTER.WHATSAPP)}
    >
      <img src={whatsappIcon} alt="" className="tw-w-4 tw-h-4" />
      {t('config.intention_editor.branches.whatsapp')}
    </button>
  </div>
);

const BranchLaneHeader = ({ channel, enabled, t }) => {
  if (!channel) return null;

  const isWhatsapp = channel === FLOW_BRANCH_CHANNEL.WHATSAPP;
  const label = isWhatsapp
    ? t('config.intention_editor.branches.whatsapp')
    : t('config.intention_editor.branches.all');

  return (
    <div className={`tw-flex tw-items-center tw-gap-2 ${isWhatsapp && !enabled ? 'tw-opacity-50' : ''}`}>
      <span className="tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center" aria-hidden>
        {isWhatsapp ? (
          <img src={whatsappIcon} alt="" className="tw-h-4 tw-w-4 tw-object-contain" />
        ) : (
          <IconMC name="world" size="base" className="tw-text-gray-500" />
        )}
      </span>
      <span className="tw-text-xs tw-font-medium tw-text-gray-700">
        {label}
      </span>
    </div>
  );
};

const IntentionStepsList = ({
  steps,
  intentionId,
  getToolDetails,
  t
}) => {
  if (!steps.length) return null;

  return (
    <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
      {steps.map((step, stepIndex) => (
        <li
          key={step.id || `${intentionId}-step-${stepIndex}`}
          className="tw-flex tw-items-start tw-gap-3"
        >
          <div className="tw-flex tw-h-7 tw-min-w-[28px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-text-xs tw-font-semibold tw-text-gray-700">
            {stepIndex + 1}
          </div>
          <div className="tw-min-w-0 tw-flex-1 tw-text-sm tw-text-gray-800 tw-whitespace-pre-wrap tw-leading-relaxed">
            {renderStepDescriptionWithPills(step, getToolDetails, t)}
          </div>
        </li>
      ))}
    </ol>
  );
};

const GlobalInstructionModalContent = ({
  instructions = [],
  initialStep = null,
  intentions = []
}) => {
  const { t } = useTranslation('ChatbotIA');
  const dispatch = useDispatch();
  const { getToolDetails } = useTools();
  const [branchFilter, setBranchFilter] = useState(OVERVIEW_BRANCH_FILTER.ALL_LANES);

  const hasAnyWhatsappBranch = useMemo(
    () => intentions.some((intention) => (
      (intention?.branches || []).some((branch) => branch.channel === FLOW_BRANCH_CHANNEL.WHATSAPP)
    )),
    [intentions]
  );

  const intentionsWithLanes = useMemo(
    () => intentions.map((intention) => {
      const lanes = visibleOverviewLanes(filterOverviewLanes(
        intentionOverviewLanes(intention),
        hasAnyWhatsappBranch ? branchFilter : OVERVIEW_BRANCH_FILTER.ALL_LANES
      ));
      const totalSteps = lanes.reduce((sum, lane) => sum + lane.steps.length, 0);
      return { intention, lanes, totalSteps };
    }),
    [intentions, branchFilter, hasAnyWhatsappBranch]
  );

  const closeAction = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: COMPONENT_KEY }
    });
  }, [dispatch]);

  return (
    <RightModalLayout
      closeAction={closeAction}
      title={t('config.global_instruction.flow_overview_modal_title')}
      showBackButton={false}
      rootClassName="tw-bg-gray-50"
      footerClassName="!tw-bg-gray-50 !tw-pt-0 !tw-pb-0"
      className="tw-space-y-0"
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <section className="tw-flex tw-flex-col tw-gap-2">
          <StaticSectionTitle>{t('config.general_instructions')}</StaticSectionTitle>
          <p className="tw-text-xs tw-font-normal tw-leading-relaxed tw-text-gray-600">
            {t('config.global_instruction.modal_panel_description')}
          </p>
          <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
            {instructions.map((instruction, index) => (
              <OverviewListRow key={instruction.id || `global-instruction-${index}`}>
                <GlobalInstructionSavedCard
                  instruction={instruction}
                  instructionNumber={index + 1}
                  showActions={false}
                  maxConditionPreviewLength={null}
                  variant="modalList"
                />
              </OverviewListRow>
            ))}
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-6">
          <StaticSectionTitle>{t('config.initial_step.title')}</StaticSectionTitle>
          <p className="tw-text-xs tw-font-normal tw-leading-relaxed tw-text-gray-600">
            {t('config.initial_step.description')}
          </p>
          <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
            {initialStep ? (
              <OverviewListRow key={initialStep.id || 'initial-step'}>
                <GlobalInstructionSavedCard
                  instruction={initialStep}
                  instructionNumber={1}
                  showActions={false}
                  maxConditionPreviewLength={null}
                  variant="modalList"
                />
              </OverviewListRow>
            ) : (
              <p className="tw-m-0 tw-text-xs tw-font-normal tw-leading-relaxed tw-text-gray-500">
                {t('config.initial_step.empty_overview')}
              </p>
            )}
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-6">
          <StaticSectionTitle>{t('config.intents')}</StaticSectionTitle>
          <p className="tw-text-xs tw-font-normal tw-leading-relaxed tw-text-gray-600">
            {t('config.global_instruction.intents_modal_panel_description')}
          </p>
          {hasAnyWhatsappBranch ? (
            <div className="tw-mt-1">
              <BranchOverviewFilter
                value={branchFilter}
                onChange={setBranchFilter}
                t={t}
              />
            </div>
          ) : null}
          <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-6">
            {intentionsWithLanes.map(({ intention, lanes, totalSteps }) => (
              <OverviewListRow key={intention.id}>
                <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3">
                  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-text-sm tw-text-gray-600">
                    <div className="tw-text-gray-950 tw-font-medium tw-leading-snug">
                      {intention.name}
                    </div>
                    {intention.description ? (
                      <div className="tw-text-xs tw-text-gray-500 tw-leading-relaxed">
                        {intention.description}
                      </div>
                    ) : null}
                    <div className="tw-text-xs tw-text-gray-500">
                      {totalSteps}
                      {' '}
                      {t('config.steps_count')}
                    </div>
                  </div>
                  {lanes.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-gap-4 tw-border-0 tw-border-l-2 tw-border-solid tw-border-gray-200 tw-pl-3">
                      {lanes.map((lane) => (
                        <div key={`${intention.id}-${lane.key}`} className="tw-flex tw-flex-col tw-gap-3">
                          {lane.channel ? (
                            <BranchLaneHeader
                              channel={lane.channel}
                              enabled={lane.enabled}
                              t={t}
                            />
                          ) : (
                            <div className="tw-text-xs tw-font-medium tw-text-gray-700">
                              {t('config.intention_editor.steps.title')}
                            </div>
                          )}
                          <IntentionStepsList
                            steps={lane.steps}
                            intentionId={`${intention.id}-${lane.key}`}
                            getToolDetails={getToolDetails}
                            t={t}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </OverviewListRow>
            ))}
          </div>
        </section>
      </div>
    </RightModalLayout>
  );
};

const GlobalInstructionModal = (props) => (
  <AgentProvider agentId={props.agentId}>
    <GlobalInstructionModalContent {...props} />
  </AgentProvider>
);

export default GlobalInstructionModal;
